import React, { useState, useEffect, useCallback, useRef } from 'react';
import useBreakpoint from './hooks/useBreakpoint';
import useAutoRefresh, { isSameData } from './hooks/useAutoRefresh';
import Header from './components/Header';
import MetricsBar from './components/MetricsBar';
import QuickAdd from './components/QuickAdd';
import Filters from './components/Filters';
import RecordsTable from './components/RecordsTable';
import EditDrawer from './components/EditDrawer';
import ImportModal from './components/ImportModal';
import Toast from './components/Toast';
import SignIn from './components/SignIn';
import ChangePasswordModal from './components/ChangePasswordModal';
import CTOTracker from './components/CTOTracker';
import FirmPipeline from './components/FirmPipeline';
import { getRecords, getMetrics, getSegments, createRecord, updateRecord, deleteRecord, bulkDeleteRecords, getMe } from './utils/api';
import { getToken, getUser, setSession, clearSession } from './utils/auth';

const PIPELINES = [
  { key: 'Design Partner', label: 'Partner Outreach' },
  { key: 'Firm',           label: 'Firm Pipeline' },
  { key: 'Investor',       label: 'Investor Outreach' },
  { key: 'CTO',            label: 'CTO Candidates' },
];

export default function App() {
  // 'checking' while a stored token is verified, so the sign-in screen never flashes.
  const [authState,   setAuthState]   = useState(() => (getToken() ? 'checking' : 'out'));
  const [user,        setUser]        = useState(() => getUser());
  const [signOutReason, setSignOutReason] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [pipeline,    setPipeline]    = useState('Design Partner');
  const [records,     setRecords]     = useState([]);
  const [metrics,     setMetrics]     = useState({});
  const [segments,    setSegments]    = useState([]);
  const [filters,     setFilters]     = useState({ status:'', stage:'', priority:'', owner:'', overdue:false, search:'' });
  const [editRecord,  setEditRecord]  = useState(null);
  const [importing,   setImporting]   = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [toast,       setToast]       = useState(null);
  const [firmReloadKey, setFirmReloadKey] = useState(0);
  const [pendingFirmId, setPendingFirmId] = useState(null);
  const [manualRefreshKey, setManualRefreshKey] = useState(0);
  const [busy,         setBusy]        = useState(false);
  const lastRefreshedRef = useRef(new Date());
  const breakpoint = useBreakpoint();
  const tabsRef = useRef(null);

  // Display only: the server takes created_by / last_updated_by from the signed-in token.
  const activeUser = user ? user.name : '';

  // Verify a stored token on load.
  useEffect(() => {
    if (!getToken()) return undefined;
    let cancelled = false;
    getMe()
      .then(me => {
        if (cancelled) return;
        setSession({ token: getToken(), user: me });
        setUser(me);
        setAuthState('in');
      })
      .catch(err => {
        if (cancelled) return;
        if (err.response?.status === 401) {
          clearSession();
          setUser(null);
          setAuthState('out');
        } else if (getUser()) {
          // Server unreachable: keep the saved session; the first real request will say if the token is bad.
          setAuthState('in');
        } else {
          clearSession();
          setAuthState('out');
        }
      });
    return () => { cancelled = true; };
  }, []);

  // api.js clears the session and fires this when the server rejects the token.
  useEffect(() => {
    const onSignedOut = (e) => {
      setUser(null);
      setSignOutReason(e.detail?.reason || '');
      setChangingPassword(false);
      setAuthState('out');
    };
    window.addEventListener('sot:signed-out', onSignedOut);
    return () => window.removeEventListener('sot:signed-out', onSignedOut);
  }, []);

  // On small screens the tab bar scrolls sideways; keep the active tab in view.
  useEffect(() => {
    if (breakpoint === 'desktop') return;
    const bar = tabsRef.current;
    const active = bar?.querySelector('[data-active="true"]');
    if (!bar || !active) return;
    bar.scrollTo({ left: active.offsetLeft - (bar.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
  }, [pipeline, breakpoint, authState]);

  // Reset filters when switching pipeline
  useEffect(() => {
    setFilters({ status:'', stage:'', priority:'', owner:'', overdue:false, search:'' });
    setRecords([]);
    setLoading(true);
  }, [pipeline]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadRecords = useCallback(async () => {
    if (authState !== 'in' || pipeline === 'CTO' || pipeline === 'Firm') return;
    try {
      const params = { type: pipeline };
      if (filters.status)   params.status   = filters.status;
      if (filters.stage)    params.stage    = filters.stage;
      if (filters.priority) params.priority = filters.priority;
      if (filters.owner)    params.owner    = filters.owner;
      if (filters.overdue)  params.overdue  = 'true';
      if (filters.search)   params.search   = filters.search;
      const data = await getRecords(params);
      setRecords(data);
      lastRefreshedRef.current = new Date();
    } catch (e) {
      showToast('Failed to load records', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, pipeline, authState]);

  const loadMetrics  = useCallback(async () => {
    if (authState !== 'in' || pipeline === 'CTO' || pipeline === 'Firm') return;
    try { setMetrics(await getMetrics({ type: pipeline })); } catch {}
  }, [pipeline, authState]);

  const loadSegments = useCallback(async () => {
    if (authState !== 'in') return;
    try { setSegments(await getSegments()); } catch {}
  }, [authState]);

  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => { loadMetrics(); loadSegments(); }, [loadMetrics, loadSegments]);

  const isRecordTab = pipeline !== 'CTO' && pipeline !== 'Firm';

  // Background refresh for the Partner and Investor tabs: same filters, no spinner, no toast,
  // and state is only set when the data actually changed.
  const silentRefresh = useCallback(async (shouldApply) => {
    const params = { type: pipeline };
    if (filters.status)   params.status   = filters.status;
    if (filters.stage)    params.stage    = filters.stage;
    if (filters.priority) params.priority = filters.priority;
    if (filters.owner)    params.owner    = filters.owner;
    if (filters.overdue)  params.overdue  = 'true';
    if (filters.search)   params.search   = filters.search;
    const [data, met] = await Promise.all([getRecords(params), getMetrics({ type: pipeline })]);
    if (!shouldApply()) return;
    setRecords(prev => (isSameData(prev, data) ? prev : data));
    setMetrics(prev => (isSameData(prev, met) ? prev : met));
  }, [filters, pipeline]);

  const markRefreshed = useCallback((at) => { lastRefreshedRef.current = at; }, []);

  // Paused while the user is mid-action: drawer, import or password modal, or a request in flight.
  const { refreshNow } = useAutoRefresh(silentRefresh, {
    enabled: authState === 'in' && isRecordTab,
    paused: !!editRecord || importing || changingPassword || busy,
    onRefreshed: markRefreshed,
  });

  const handleHeaderRefresh = () => {
    if (isRecordTab) {
      refreshNow().catch(() => showToast('Failed to load records', 'error'));
    } else {
      setManualRefreshKey(k => k + 1);
    }
  };

  const handleQuickAdd = async (firmName) => {
    setBusy(true);
    try {
      const rec = await createRecord({ type: pipeline, firm_name: firmName });
      showToast(`✓ "${firmName}" added`);
      await loadRecords(); await loadMetrics();
      setEditRecord(rec);
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to add record', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (id, data) => {
    try {
      const updated = await updateRecord(id, data);
      setRecords(prev => prev.map(r => r.id === id ? updated : r));
      setEditRecord(null);
      showToast('✓ Saved');
      loadMetrics();
    } catch (e) {
      if (e.response?.status === 409) showToast(e.response.data.message, 'warning');
      else if (e.response?.status === 400) showToast(e.response.data.error, 'error');
      else showToast('Save failed', 'error');
      throw e;
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRecord(id);
      setRecords(prev => prev.filter(r => r.id !== id));
      setEditRecord(null);
      showToast('Record deleted');
      loadMetrics();
    } catch { showToast('Delete failed', 'error'); }
  };

  const handleBulkDelete = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} record${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return false;
    setBusy(true);
    try {
      await bulkDeleteRecords(ids);
      setRecords(prev => prev.filter(r => !ids.includes(r.id)));
      showToast(`✓ ${ids.length} record${ids.length !== 1 ? 's' : ''} deleted`);
      loadMetrics();
      return true;
    } catch { showToast('Bulk delete failed', 'error'); return false; }
    finally { setBusy(false); }
  };

  const openDesignPartner = (record) => {
    setPipeline('Design Partner');
    setEditRecord(record);
  };

  const openFirmRecord = (firmId) => {
    setPipeline('Firm');
    setEditRecord(null);
    setPendingFirmId(firmId);
  };

  const handleImportDone = async (summary) => {
    setImporting(false);
    showToast(`Import complete: ${summary.added} added, ${summary.updated} updated, ${summary.skipped} skipped`);
    if (pipeline === 'Firm') {
      setFirmReloadKey(k => k + 1);
    } else {
      await loadRecords(); await loadMetrics(); await loadSegments();
    }
  };

  const handleSignOut = () => {
    clearSession();
    setUser(null);
    setSignOutReason('');
    setChangingPassword(false);
    setAuthState('out');
  };

  const handleSignedIn = (me) => {
    setUser(me);
    setSignOutReason('');
    setAuthState('in');
  };

  if (authState === 'checking') {
    return (
      <div role="status" style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontFamily: 'DM Sans, sans-serif', fontSize: 14 }}>
        Loading…
      </div>
    );
  }
  if (authState === 'out') return <SignIn onSignedIn={handleSignedIn} reason={signOutReason} />;

  return (
    <div className="app-root" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <style>{`* { box-sizing: border-box; }`}</style>
      <Header
        activeUser={activeUser}
        onChangePassword={() => setChangingPassword(true)}
        onImport={() => setImporting(true)}
        onRefresh={handleHeaderRefresh}
        getLastRefreshed={() => lastRefreshedRef.current}
        showImport={pipeline !== 'CTO'}
        onSignOut={handleSignOut}
      />

      {/* Pipeline tabs */}
      <div ref={tabsRef} className="app-tabs" style={{
        background: 'var(--bg-card)',
        borderBottom: '1px solid var(--border)',
        padding: '0 24px',
        display: 'flex', gap: 4,
      }}>
        {PIPELINES.map(p => (
          <button
            key={p.key}
            className="app-tab"
            data-active={pipeline === p.key ? 'true' : 'false'}
            onClick={() => setPipeline(p.key)}
            style={{
              background: 'none', border: 'none',
              borderBottom: pipeline === p.key ? '2px solid var(--primary)' : '2px solid transparent',
              color: pipeline === p.key ? 'var(--primary)' : 'var(--text-muted)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: pipeline === p.key ? 600 : 400,
              fontSize: 13, padding: '12px 16px',
              cursor: 'pointer', transition: 'all 0.15s',
              marginBottom: -1,
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <main className="app-main" style={{ flex: 1, minHeight: 0, padding: '0 24px 40px', maxWidth: 1600, width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {pipeline === 'CTO' ? (
          <CTOTracker showToast={showToast} refreshKey={manualRefreshKey} onRefreshed={markRefreshed} />
        ) : pipeline === 'Firm' ? (
          <FirmPipeline
            showToast={showToast} reloadKey={firmReloadKey}
            refreshKey={manualRefreshKey} onRefreshed={markRefreshed}
            onOpenDesignPartner={openDesignPartner}
            openFirmId={pendingFirmId}
            onOpenFirmHandled={() => setPendingFirmId(null)}
          />
        ) : (
          <>
            <MetricsBar metrics={metrics} pipeline={pipeline} filters={filters} onChange={setFilters} />
            <QuickAdd onAdd={handleQuickAdd} pipeline={pipeline} />
            <Filters filters={filters} onChange={setFilters} segments={segments} pipeline={pipeline} />
            <div className={`pipeline-view${breakpoint === 'phone' ? ' cards' : ''}`} style={{ flex: 1, minHeight: 240 }}>
              <RecordsTable
                records={records} loading={loading}
                onEdit={setEditRecord}
                onBulkDelete={handleBulkDelete}
                pipeline={pipeline}
              />
            </div>
          </>
        )}
      </main>

      {editRecord && (
        <EditDrawer
          record={editRecord} segments={segments}
          activeUser={activeUser} pipeline={pipeline}
          onSave={handleSave} onDelete={handleDelete}
          onClose={() => setEditRecord(null)}
          onSegmentAdded={loadSegments}
          onOpenFirm={openFirmRecord}
        />
      )}

      {importing && pipeline !== 'CTO' && (
        <ImportModal
          pipeline={pipeline}
          onDone={handleImportDone}
          onClose={() => setImporting(false)}
        />
      )}

      {changingPassword && (
        <ChangePasswordModal
          onClose={() => setChangingPassword(false)}
          onChanged={() => { setChangingPassword(false); showToast('✓ Password changed'); }}
        />
      )}

      {toast && <Toast message={toast.msg} type={toast.type} />}
    </div>
  );
}